// Offline write queue per PRD §5.4.
// On write failure due to NetworkError, push the request onto a localStorage queue.
// Drain on `online` events. Reads simply fail.

import { ApiError, NetworkError } from "./client.js";

const QUEUE_KEY = "trip-planner-offline-queue-v1";

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(q) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    // Storage full or disabled — silently drop the queue.
  }
}

export function enqueueWrite(description) {
  const q = readQueue();
  q.push({ ...description, id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, queuedAt: Date.now() });
  writeQueue(q);
}

export function getQueueSize() {
  return readQueue().length;
}

export function clearQueue() {
  writeQueue([]);
}

// Run a function and, if it throws NetworkError, queue it. Returns true if drained,
// false if queued. Caller is responsible for keeping local state consistent (optimistic
// state survives across reloads only if the caller persists it; we don't here).
export async function tryWrite(fn, queueDescriptor, { onQueued } = {}) {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof NetworkError) {
      enqueueWrite(queueDescriptor);
      onQueued?.(err);
      return null;
    }
    throw err;
  }
}

// Drain queued writes by re-issuing them through the provided handler map.
// `handlers` maps endpoint key -> async (descriptor) => Promise<void>.
// Returns { drained, remaining }.
export async function drainQueue(handlers) {
  const q = readQueue();
  if (q.length === 0) return { drained: 0, remaining: 0 };
  const remaining = [];
  let drained = 0;
  for (const entry of q) {
    const handler = handlers[entry.kind];
    if (!handler) {
      remaining.push(entry);
      continue;
    }
    try {
      await handler(entry);
      drained++;
    } catch (err) {
      if (err instanceof NetworkError) {
        remaining.push(entry);
      } else if (err instanceof ApiError) {
        // Permanent failure (validation, not-found) — drop the entry.
        drained++;
        // eslint-disable-next-line no-console
        console.warn("Dropping queued write:", entry, err);
      } else {
        remaining.push(entry);
      }
    }
  }
  writeQueue(remaining);
  return { drained, remaining: remaining.length };
}
